import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';
import { ApiError, useAuth } from '@invite/app-core';
import { bookStay, createBlock, getMe } from '../api/ownerApi';
import { ownerKeys } from '../api/queryKeys';
import type { CalendarBooking, LockedPeriod, OwnerCalendar } from '../api/schemas';
import type { IsoDate } from '../calendar/monthGrid';
import { useT } from '../i18n/useT';
import { Sheet } from '../ui/Sheet';
import { useUnit } from '../units/UnitContext';
import { isTypeFull, signOutAfterMessage } from './apiErrors';
import { BillingStep } from './BillingStep';
import {
  steps,
  TYPE_FULL,
  toBlockRequest,
  toRequest,
  validateStep,
  type BookingDraft,
  type Step
} from './bookingForm';
import { DEFAULT_COUNTRY } from './phone';
import { ConfirmStep } from './ConfirmStep';
import { WhenStep } from './WhenStep';
import { WhoStep } from './WhoStep';

/** How long the session-expired message stays readable before the app signs the owner out. */

/** Taps on Book this soon after Confirm appears are ignored: a double tap on Next must not book. */
const CONFIRM_SETTLE_MS = 500;

interface BookingSheetProps {
  open: boolean;
  initialFrom?: IsoDate;
  /** Loaded bookings of the visible grid, for the fast overlap check. */
  bookings: CalendarBooking[];
  /** Fully booked nights of the visible grid, checked the same way. */
  locked: LockedPeriod[];
  today: IsoDate;
  onClose: () => void;
}

export const BookingSheet = ({
  open,
  initialFrom,
  bookings,
  locked,
  today,
  onClose
}: BookingSheetProps): JSX.Element => {
  const t = useT();
  const [busy, setBusy] = useState(false);
  return (
    <Sheet open={open} title={t('Book stay')} onClose={onClose} dismissible={!busy}>
      {/* Mounted only while open, so every opening starts fresh with its own idempotency key. */}
      <BookingForm
        initialFrom={initialFrom}
        bookings={bookings}
        locked={locked}
        today={today}
        onClose={onClose}
        onBusyChange={setBusy}
      />
    </Sheet>
  );
};

type BookingFormProps = Omit<BookingSheetProps, 'open'> & {
  onBusyChange: (busy: boolean) => void;
};

const overlaps = (from: string, to: string, b: { from: string; to: string }): boolean =>
  from < b.to && b.from < to;

const BookingForm = ({
  initialFrom,
  bookings,
  locked,
  today,
  onClose,
  onBusyChange
}: BookingFormProps): JSX.Element => {
  const t = useT();
  const { unit } = useUnit();
  const { principal } = useAuth();
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ownerKeys.me(principal?.teamId ?? null),
    queryFn: getMe,
    retry: false
  });
  const needsBilling = me.data?.hasBillingCustomer === false;

  // One key per request body: a retry of the same body reuses it (the server answers the first
  // attempt again), an edited body is a new booking and gets a new key.
  const lastAttempt = useRef<{ body: string; key: string } | null>(null);
  const keyFor = (body: string): string => {
    if (lastAttempt.current?.body !== body) {
      lastAttempt.current = { body, key: crypto.randomUUID() };
    }
    return lastAttempt.current.key;
  };
  const [index, setIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<BookingDraft>(() => ({
    kind: 'stay',
    bookingFor: 'self',
    visitorName: '',
    visitorPhone: '',
    visitorCountry: DEFAULT_COUNTRY,
    from: initialFrom ?? '',
    to: '',
    comment: '',
    street: '',
    postcode: '',
    city: '',
    billingPhone: '',
    billingCountry: DEFAULT_COUNTRY
  }));
  const flow = steps(needsBilling, draft.kind);
  const isBlock = draft.kind === 'block';

  const submit = useMutation({
    mutationFn: () => {
      if (draft.kind === 'block') {
        // The server answers a repeat of the same dates with the block already made.
        return createBlock(unit.id, toBlockRequest(draft));
      }
      const body = JSON.stringify(toRequest(draft, '', needsBilling));
      return bookStay(unit.id, toRequest(draft, keyFor(body), needsBilling));
    },
    onSuccess: (created) => {
      // Visible at once; the refetch below confirms it.
      queryClient.setQueriesData<OwnerCalendar>(
        {
          predicate: (q) => {
            const [scope, kind, unitId, from, to] = q.queryKey;
            return (
              scope === 'owner' &&
              kind === 'calendar' &&
              unitId === unit.id &&
              typeof from === 'string' &&
              typeof to === 'string' &&
              overlaps(from, to, created)
            );
          }
        },
        (old) =>
          old === undefined ||
          old.bookings.some((b) => b.id === created.id && b.type === created.type)
            ? old
            : { ...old, bookings: [...old.bookings, created] }
      );
      void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unit.id) });
      void queryClient.invalidateQueries({ queryKey: ownerKeys.me(principal?.teamId ?? null) });
      onClose();
    },
    onError: (error) => {
      if (!(error instanceof ApiError) || error.status >= 500 || isTypeFull(error)) {
        // Unconfirmed: the first attempt may have landed. Show the owner what the server has.
        void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unit.id) });
      }
      if (error instanceof ApiError && error.status === 401) {
        signOutAfterMessage();
      }
    }
  });

  const form = useRef<HTMLFormElement>(null);
  const confirmShownAt = useRef(0);
  const focusInvalid = useRef(false);
  const stepEntered = useRef(false);
  const progress = useRef<HTMLDivElement>(null);
  const failureRef = useRef<HTMLParagraphElement>(null);
  const wasPending = useRef(false);

  const pending = submit.isPending;
  // The Sheet refuses to close while a request is in flight; a closing sheet unmounts this form.
  useEffect(() => {
    onBusyChange(pending);
    return () => {
      onBusyChange(false);
    };
  }, [pending, onBusyChange]);
  // The Book button is gone while pending: focus the status, then the error (or Book) afterwards.
  useEffect(() => {
    if (pending) {
      wasPending.current = true;
      progress.current?.focus();
    } else if (wasPending.current) {
      wasPending.current = false;
      if (failureRef.current !== null) {
        failureRef.current.focus();
      } else {
        form.current?.querySelector<HTMLElement>('button[type="submit"]')?.focus();
      }
    }
  }, [pending]);

  // Focus follows the step: the new heading after Next/Back, the first invalid field after a
  // failed Next. The first render is left to the Sheet's own focus handling.
  useEffect(() => {
    if (!stepEntered.current) {
      stepEntered.current = true;
      return;
    }
    form.current?.querySelector<HTMLElement>('[data-step-heading]')?.focus();
  }, [index]);
  useEffect(() => {
    if (focusInvalid.current) {
      focusInvalid.current = false;
      form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    }
  }, [errors]);

  const step: Step = flow[Math.min(index, flow.length - 1)] ?? 'when';
  const last = index >= flow.length - 1;

  const change = (patch: Partial<BookingDraft>): void => {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([key]) => !(key in patch))));
  };

  const next = (at: number): void => {
    const found = validateStep(step, draft, bookings, today, locked);
    setErrors(found);
    if (Object.keys(found).length === 0) {
      if (index + 1 >= flow.length - 1) {
        confirmShownAt.current = at;
      }
      setIndex(index + 1);
    } else {
      focusInvalid.current = true;
    }
  };

  const failure = ((): string | null => {
    const error = submit.error;
    if (error === null) {
      return null;
    }
    if (error instanceof ApiError) {
      if (error.status === 401) {
        return t('Your session has expired. Please sign in again.');
      }
      if (isTypeFull(error)) {
        return t(TYPE_FULL);
      }
      // The backend's message says what to change (dates taken, a date rule, module switched
      // off) or that it could not confirm (502); a bare 500 gets the generic text.
      if (
        ((error.status >= 400 && error.status < 500) || error.status === 502) &&
        error.message !== ''
      ) {
        return error.message;
      }
    }
    return t('Something went wrong. Please try again.');
  })();

  if (me.isError) {
    // Without the profile the sheet cannot tell whether the owner's details are needed.
    return (
      <div className="ow-error-row" role="alert">
        <p className="ow-error">{t("We couldn't load your details.")}</p>
        <button
          type="button"
          className="ow-btn-secondary"
          onClick={() => {
            void me.refetch();
          }}
        >
          {t('Try again')}
        </button>
      </div>
    );
  }

  return (
    <form
      ref={form}
      className="ow-form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!last) {
          next(event.timeStamp);
        } else if (
          !submit.isPending &&
          event.timeStamp - confirmShownAt.current >= CONFIRM_SETTLE_MS
        ) {
          submit.mutate();
        }
      }}
    >
      <p className="ow-muted ow-step-count">
        {t('Step {0} of {1}', { 0: index + 1, 1: flow.length })}
      </p>
      {step === 'who' && <WhoStep draft={draft} errors={errors} onChange={change} />}
      {step === 'when' && <WhenStep draft={draft} errors={errors} onChange={change} />}
      {step === 'billing' && <BillingStep draft={draft} errors={errors} onChange={change} />}
      {step === 'confirm' && <ConfirmStep draft={draft} ownerName={me.data?.name ?? null} />}
      {failure !== null && (
        <p ref={failureRef} className="ow-error" role="alert" tabIndex={-1}>
          {failure}
        </p>
      )}
      {pending ? (
        <div ref={progress} className="ow-progress" role="status" aria-live="polite" tabIndex={-1}>
          <span className="ow-spinner" aria-hidden="true" />
          <div>
            <strong>{isBlock ? t('Blocking the dates…') : t('Booking your stay…')}</strong>
            <p className="ow-muted">
              {t("We're confirming it with the booking system. This can take up to a minute.")}
            </p>
          </div>
        </div>
      ) : (
        <div className="ow-step-actions">
          {index > 0 && (
            <button
              type="button"
              className="ow-btn-secondary"
              disabled={pending}
              onClick={() => {
                submit.reset();
                setIndex(index - 1);
              }}
            >
              {t('Back')}
            </button>
          )}
          {last ? (
            <button key="book" type="submit" className="ow-btn-primary" disabled={me.isPending}>
              {isBlock ? t('Block dates') : t('Book')}
            </button>
          ) : (
            <button key="next" type="submit" className="ow-btn-primary" disabled={me.isPending}>
              {t('Next')}
            </button>
          )}
        </div>
      )}
    </form>
  );
};
